/**
 * HSE / safety write layer + full-job roll-up — injected onto the shared Oxbo-P
 * client so the contract files (`@api/pApi`, `@api/types`) stay untouched and
 * branches merge cleanly.
 *
 * Oxbo-P owns the record; the FE only submits the as-entered day header and
 * displays what P serves back. PUT keys on `entity_id` (the stable business key
 * across versions), never the row `id`.
 */
import { pApi } from "@api/pApi";

/**
 * A HSE day header as served by the write endpoints. The shared `SafetyOut`
 * (in `@api/types`) predates the write layer and omits `entity_id`/`version`;
 * the write contract adds them, so we mirror the full wire shape locally.
 */
export interface SafetyRowOut {
  id: string;
  entity_id: string;
  version: number;
  event_id: string;
  daily_report_id: string;
  well_uwi: string;
  date_report: string | null;
  days_since_lti_operator: number | null;
  days_since_lti_total: number | null;
  rid_cards: number | null;
  job_safety_analysis: number | null;
  jobsite_evaluation: number | null;
  bop_pressure_annular: number | null;
  last_casing_test_pressure: number | null;
  is_company_inspected: boolean | null;
  is_government_inspected: boolean | null;
}

/** POST/PUT body for a HSE day header. All fields optional on the wire. */
export interface SafetyWrite {
  date_report?: string | null;
  days_since_lti_operator?: number | null;
  days_since_lti_total?: number | null;
  rid_cards?: number | null;
  job_safety_analysis?: number | null;
  jobsite_evaluation?: number | null;
  bop_pressure_annular?: number | null;
  last_casing_test_pressure?: number | null;
  is_company_inspected?: boolean | null;
  is_government_inspected?: boolean | null;
  change_reason?: string | null;
}

/** GET /wells/{well_uwi}/full-job/hse — the whole-well HSE roll-up. */
export interface FullJobHseResponse {
  well_uwi: string;
  unit_profile_key: string | null;
  days_since_lti_operator: number | null;
  days_since_lti_total: number | null;
  items: SafetyRowOut[];
}

export const hseApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // Whole-well HSE roll-up (§full-job): header LTI counters + every day's
    // safety header across the job. Server-assembled; display-only.
    getFullJobHse: b.query<FullJobHseResponse, string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/full-job/hse`,
    }),

    // Create the day's HSE header (report-scoped). Server derives event/well
    // from the report. Refetch the event's safety trail on success.
    createSafety: b.mutation<SafetyRowOut, { reportId: string; body: SafetyWrite }>({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/safety`,
        method: "POST",
        body,
      }),
    }),

    // Update the day's HSE header, keyed on entity_id (stable business key).
    updateSafety: b.mutation<
      SafetyRowOut,
      { reportId: string; entityId: string; body: SafetyWrite }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/safety/${entityId}`,
        method: "PUT",
        body,
      }),
    }),
  }),
});

export const {
  useGetFullJobHseQuery,
  useCreateSafetyMutation,
  useUpdateSafetyMutation,
} = hseApi;
